import { ChangeEvent, useCallback, useEffect, useState } from 'react'

import { useDebouncedCallback } from './useDebouncedCallback'

const SEARCH_DEBOUNCE_MS = 500

export const useDebouncedSearchInput = (
  externalValue: string | undefined,
  onValueChange: (value: string) => void,
): { onChange: (event: ChangeEvent<HTMLInputElement>) => void; value: string } => {
  const [localValue, setLocalValue] = useState(externalValue ?? '')

  const debouncedChange = useDebouncedCallback((value: string) => {
    onValueChange(value)
  }, SEARCH_DEBOUNCE_MS)

  useEffect(() => {
    debouncedChange.cancel()
    setLocalValue(externalValue ?? '')

    return () => {
      debouncedChange.cancel()
    }
  }, [debouncedChange, externalValue])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setLocalValue(value)

      if (value === '') {
        debouncedChange.cancel()
        onValueChange('')
      } else {
        debouncedChange(value)
      }
    },
    [debouncedChange, onValueChange],
  )

  return { onChange: handleChange, value: localValue }
}
